<template>
  <div class="h-100">
    <app-home />

    <!-- Dialog for general messages -->
    <custom-dialog
      v-model="dialog.show"
      :title="dialog.title"
      :text="dialog.text"
      :action="dialog.action"
    />

    <!-- Terms of Service Dialog -->
    <to-s-dialog
      v-model="ToSDialog.show"
      :content="ToSContent"
      @accept="acceptTos"
      @decline="declineTos"
    />

    <!-- Subscription Dialog -->
    <v-dialog
      v-model="subscriptionDialog.show"
      max-width="800"
      v-if="!isSharedLink()"
    >
      <v-card>
        <PurchaseSubscription />
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import CustomDialog from "@/components/common/Dialog.vue";
import ToSDialog from "@/components/common/ToSDialog.vue";
import { createRgbVarsForThemes } from "@/js/utils.vuetify.css";
import { getPredictors, getUserSettings } from "@/js/api.schema";

import * as wsSingle from "@/js/api.websocket.single";
import {
  disconnect as disconnectSriesDownload,
  openSeriesWebsocket as openDownloadWs
} from "@/js/api.series.download";
import {
  disconnect as disconnectSriesUpload,
  openSeriesWebsocket as openUploadWs
} from "@/js/api.series.upload";
import AppHome from "@/views/Home";
import { mapGetters } from "vuex";
import Vue from "vue";
import PurchaseSubscription from "@/components/PurchaseSubscription.vue";
import ToSString from "./ToS.js"; // if in same directory

export default {
  name: "App",
  components: { AppHome, CustomDialog, ToSDialog, PurchaseSubscription },
  data() {
    return {
      dialog: { show: false, title: "", text: "", action: null },
      ToSDialog: { show: false },
      subscriptionDialog: { show: false }
      // reconnectionCounter: 0
    };
  },
  created() {
    // Connect to websocket, disconnect on page unload

    try {
      wsSingle.disconnect();
      disconnectSriesDownload();
      disconnectSriesUpload();
    } catch (error) {
      // Handle the error here, you can log it or perform other actions.
      console.error("An error occurred:", error);
    }
  },
  mounted() {
    createRgbVarsForThemes(this.$vuetify.theme.themes.light);

    // Slight delay to ensure all components are ready
    setTimeout(() => {
      if (this.isAuth && !this.hasPaidSubscription) {
        this.subscriptionDialog.show = true;
      }

      // Set 5-minute interval
      this.subscriptionCheckInterval = setInterval(() => {
        if (this.isAuth && !this.hasPaidSubscription) {
          this.subscriptionDialog.show = true;
        }
      }, 5 * 60 * 1000); // 5 minutes
    }, 500); // Adjust delay if needed
  },
  computed: {
    ...mapGetters("auth", ["isAuth"]),
    hasAcceptedTos() {
      // Check if the user has accepted the TOS from the user object
      return this.$store.state.auth.user?.accepted_tos || false;
    },
    ToSContent() {
      return ToSString; // Ensure Vue tracks it
    },
    hasPaidSubscription() {
      return this.$store.state.auth.user?.subscription_tier !== "free";
    }
  },
  methods: {
    isSharedLink() {
      return !!this.$route.params.sharedId;
    },
    isDownloadEnabled() {
      return true; //return !this.isAuth;
    },
    isUploadEnabled() {
      return true; // return this.isAuth && !this.$isDemoUser;
    },
    connectWebsocket() {
      const onClose = event => {
        this.dialog = {
          action: {
            onClick: () => {
              this.dialog.show = false;
              this.connectWebsocket();
            },
            title: "try-again"
          },
          show: true,
          text: `URL:${event.target.url} EVENT:${event.type}`,
          title: "Websocket connection error"
        };
      };
      const onError = event =>
        (this.dialog = {
          show: true,
          text: `URL:${event.target.url} EVENT:${event.type}`,
          title: "Websocket connection error"
        });

      const userId =
        (this.$store.state.auth.user && this.$store.state.auth.user.uuid) ||
        this.user;
      if (this.isDownloadEnabled())
        openDownloadWs(userId, onClose, onError, this.$router);
      if (this.isUploadEnabled()) openUploadWs(userId, onClose, onError);
    },
    async loadPredictors() {
      // get all predictors
      const predictors = await getPredictors();
      this.$store.commit("viewer/initPredictors", predictors);
    },
    loadUserSettings() {
      getUserSettings()
        .then(data => {
          // update store values
          this.$store.commit("viewer/initOptions", data);
        })
        .catch(error => console.error(error));
    },
    async acceptTos() {
      try {
        // Call the backend API to update the TOS acceptance status
        await Vue.$http.patch("auth/users/accept-tos", { accepted_tos: true });

        // Update the user object in Vuex
        this.$store.commit("auth/updateUser", { accepted_tos: true });
        // Close the TOS dialog
        this.ToSDialog.show = false;
      } catch (error) {
        console.error("Failed to update TOS acceptance status:", error);
      }
    },
    declineTos() {
      // Handle TOS decline (e.g., log out the user or show a warning)
      this.ToSDialog.show = false;
      this.$store.dispatch("auth/logout");
      this.$router.push("/login");
    }
  },

  watch: {
    isAuth(value) {
      this.$store.commit("viewer/initViewerSessionId");
      if (value) {
        this.loadPredictors();
        this.loadUserSettings();
        this.connectWebsocket();

        // Show TOS dialog if the user hasn't accepted it yet
        if (!this.hasAcceptedTos) {
          this.ToSDialog.show = true;
        }
      }
    }
  }
};
</script>

<style lang="scss">
@import "@/styles/utils.scss";
@import "@/styles/fonts.scss";
@import "@/styles/main.scss";

.tos-content {
  margin: 1rem 0;

  .tos-scrollable {
    max-height: 300px;
    overflow-y: auto;
    padding: 1rem;
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 4px;
    white-space: pre-wrap;
  }
}
</style>
